import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Youtube, Play, Trash2, Edit2, Send, Sparkles, CheckCircle,
  AlertCircle, Clock, Users, RefreshCw, ExternalLink, Bell,
  Video, Upload, Calendar, Download, X, TrendingUp, ListChecks,
  CheckSquare, FileText, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function YouTubeAdmin() {
  const [youtubeConnected, setYoutubeConnected] = useState(false);
  const [youtubeChannel, setYoutubeChannel] = useState(null);
  const [refreshingChannel, setRefreshingChannel] = useState(false);
  const [content, setContent] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [generatingWeekly, setGeneratingWeekly] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [topic, setTopic] = useState('');
  const [selectedContent, setSelectedContent] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [loadingTrending, setLoadingTrending] = useState(false);
  const [contentQueue, setContentQueue] = useState({ pending_review: [], approved: [], scheduled: [] });
  const [activeTab, setActiveTab] = useState('content'); // 'content' or 'queue'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statusRes, contentRes, subscribersRes, queueRes] = await Promise.all([
        axios.get(`${API_URL}/api/youtube/status`, { headers }),
        axios.get(`${API_URL}/api/youtube/content`, { headers }),
        axios.get(`${API_URL}/api/youtube/subscribers`, { headers }),
        axios.get(`${API_URL}/api/youtube/content-queue`, { headers }).catch(() => ({ data: { queue: {} } }))
      ]);

      setYoutubeConnected(statusRes.data.connected);
      if (statusRes.data.connected) {
        setYoutubeChannel({
          id: statusRes.data.channel_id,
          title: statusRes.data.channel_title,
          handle: statusRes.data.channel_handle,
          url: statusRes.data.channel_url,
          subscribers: statusRes.data.channel_subscriber_count,
          videoCount: statusRes.data.channel_video_count,
        });
      } else {
        setYoutubeChannel(null);
      }
      setContent(contentRes.data.content || []);
      setSubscribers(subscribersRes.data.subscribers || []);
      setContentQueue(queueRes.data.queue || { pending_review: [], approved: [], scheduled: [] });
    } catch (error) {
      console.error('Failed to load YouTube data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshChannelInfo = async () => {
    setRefreshingChannel(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/youtube/refresh-channel-info`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setYoutubeChannel({
        id: res.data.channel_id,
        title: res.data.channel_title,
        handle: res.data.channel_handle,
        url: res.data.channel_url,
        subscribers: res.data.channel_subscriber_count,
        videoCount: res.data.channel_video_count,
      });
      toast.success(`Connected to: ${res.data.channel_title}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch channel info');
    } finally {
      setRefreshingChannel(false);
    }
  };

  const loadTrendingTopics = async () => {
    setLoadingTrending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/youtube/trending-topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrendingTopics(res.data.topics || []);
    } catch (error) {
      console.error('Failed to load trending topics:', error);
    } finally {
      setLoadingTrending(false);
    }
  };

  const generateWeeklyContent = async () => {
    setGeneratingWeekly(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/youtube/generate-weekly-content`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Generated ${res.data.content?.length || 0} videos for review!`);
      loadData();
      setActiveTab('queue');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate weekly content');
    } finally {
      setGeneratingWeekly(false);
    }
  };

  const approveContent = async (contentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/youtube/approve/${contentId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Content approved! Generate video next.');
      loadData();
    } catch (error) {
      toast.error('Failed to approve content');
    }
  };

  const connectYouTube = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/youtube/auth-url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error('Failed to start YouTube connection');
    }
  };

  const generateContent = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/youtube/generate-content`,
        { topic: topic || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('AI content generated!');
      setTopic('');
      loadData();
      setSelectedContent({ ...res.data.content, content_id: res.data.content_id });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const generateVideo = async (contentId) => {
    setGeneratingVideo(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/youtube/generate-video/${contentId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Video generated with Sora 2!');
      loadData();
      if (selectedContent?.content_id === contentId) {
        setSelectedContent(prev => ({
          ...prev,
          video_url: res.data.video_url,
          video_status: 'ready'
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate video');
    } finally {
      setGeneratingVideo(false);
    }
  };

  const uploadToYouTube = async (contentId) => {
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/youtube/upload/${contentId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Video uploaded to YouTube!');
      loadData();
      if (selectedContent?.content_id === contentId) {
        setSelectedContent(prev => ({
          ...prev,
          youtube_video_id: res.data.youtube_video_id,
          youtube_url: res.data.youtube_url,
          status: 'published',
          upload_status: 'completed'
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload to YouTube');
    } finally {
      setUploading(false);
    }
  };

  const scheduleUpload = async (contentId) => {
    if (!scheduleDate) {
      toast.error('Please select a date and time');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/youtube/schedule/${contentId}?scheduled_at=${scheduleDate}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Video scheduled for upload!');
      setShowScheduler(false);
      setScheduleDate('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule upload');
    }
  };

  const cancelSchedule = async (contentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/youtube/cancel-schedule/${contentId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Schedule cancelled');
      loadData();
    } catch (error) {
      toast.error('Failed to cancel schedule');
    }
  };

  const deleteContent = async (contentId) => {
    if (!window.confirm('Delete this content?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/youtube/content/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content deleted');
      loadData();
      if (selectedContent?.content_id === contentId) {
        setSelectedContent(null);
      }
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const notifySubscribers = async (contentId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/youtube/notify-subscribers?content_id=${contentId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Failed to notify subscribers');
    }
  };

  const updateContent = async () => {
    if (!selectedContent) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/youtube/content/${selectedContent.content_id}`,
        {
          title: selectedContent.title,
          description: selectedContent.description,
          script: selectedContent.script,
          tags: selectedContent.tags,
          status: selectedContent.status
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Content updated!');
      setEditMode(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update content');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#00F0FF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <h3 className="text-lg md:text-xl font-bold flex flex-wrap items-center gap-2">
          <Youtube className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
          <span>YouTube Automation</span>
          <span className="text-xs bg-[#00F0FF]/20 text-[#00F0FF] px-2 py-1 rounded-full">
            Sora 2
          </span>
        </h3>
        <div className="flex items-center gap-2">
          {youtubeConnected ? (
            <div className="flex flex-col items-end gap-1" data-testid="youtube-connected-channel">
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-2 text-green-400 text-xs md:text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Connected
                </span>
                <Button
                  onClick={refreshChannelInfo}
                  disabled={refreshingChannel}
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  data-testid="youtube-verify-channel-btn"
                  title="Re-fetch the connected channel from YouTube. Use this to verify uploads are going to the right channel (not a hidden brand account)."
                >
                  {refreshingChannel ? 'Verifying…' : 'Verify channel'}
                </Button>
              </div>
              {youtubeChannel?.title ? (
                <a
                  href={youtubeChannel.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] md:text-xs text-neutral-400 hover:text-[#00F0FF] underline-offset-2 hover:underline"
                  data-testid="youtube-channel-link"
                >
                  → {youtubeChannel.title}
                  {youtubeChannel.handle ? ` (${youtubeChannel.handle})` : ''}
                  {' · '}
                  {youtubeChannel.subscribers ?? '0'} subs · {youtubeChannel.videoCount ?? '0'} videos
                </a>
              ) : (
                <button
                  onClick={refreshChannelInfo}
                  disabled={refreshingChannel}
                  className="text-[11px] md:text-xs text-yellow-400 hover:text-yellow-300 underline"
                  data-testid="youtube-channel-unknown"
                >
                  Channel unknown — click to verify
                </button>
              )}
            </div>
          ) : (
            <Button onClick={connectYouTube} size="sm" className="bg-red-600 hover:bg-red-700">
              <Youtube className="w-4 h-4 mr-1 md:mr-2" />
              <span className="text-sm">Connect</span>
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
        <div className="flex md:grid md:grid-cols-5 gap-3 md:gap-4 min-w-max md:min-w-0">
          <div className="bg-neutral-800/50 rounded-xl p-3 md:p-4 w-[100px] md:w-auto flex-shrink-0 md:flex-shrink">
            <Users className="w-4 h-4 md:w-5 md:h-5 text-[#00F0FF] mb-1 md:mb-2" />
            <p className="text-xl md:text-2xl font-bold">{subscribers.length}</p>
            <p className="text-xs text-neutral-400">Subs</p>
          </div>
          <div className="bg-neutral-800/50 rounded-xl p-3 md:p-4 w-[100px] md:w-auto flex-shrink-0 md:flex-shrink">
            <Play className="w-4 h-4 md:w-5 md:h-5 text-green-500 mb-1 md:mb-2" />
            <p className="text-xl md:text-2xl font-bold">{content.filter(c => c.status === 'published').length}</p>
            <p className="text-xs text-neutral-400">Published</p>
          </div>
          <div className="bg-neutral-800/50 rounded-xl p-3 md:p-4 w-[100px] md:w-auto flex-shrink-0 md:flex-shrink">
            <Calendar className="w-4 h-4 md:w-5 md:h-5 text-yellow-500 mb-1 md:mb-2" />
            <p className="text-xl md:text-2xl font-bold">{contentQueue.scheduled?.length || 0}</p>
            <p className="text-xs text-neutral-400">Scheduled</p>
          </div>
          <div className="bg-neutral-800/50 rounded-xl p-3 md:p-4 w-[100px] md:w-auto flex-shrink-0 md:flex-shrink">
            <ListChecks className="w-4 h-4 md:w-5 md:h-5 text-orange-500 mb-1 md:mb-2" />
            <p className="text-xl md:text-2xl font-bold">{contentQueue.pending_review?.length || 0}</p>
            <p className="text-xs text-neutral-400">Pending</p>
          </div>
          <div className="bg-neutral-800/50 rounded-xl p-3 md:p-4 w-[100px] md:w-auto flex-shrink-0 md:flex-shrink">
            <Clock className="w-4 h-4 md:w-5 md:h-5 text-neutral-400 mb-1 md:mb-2" />
            <p className="text-xl md:text-2xl font-bold">{content.filter(c => c.status === 'draft').length}</p>
            <p className="text-xs text-neutral-400">Drafts</p>
          </div>
        </div>
      </div>

      {/* Weekly Content Generator */}
      <div className="bg-gradient-to-r from-red-500/10 to-white/10 border border-red-500/30 rounded-xl p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <div>
            <h4 className="font-bold flex flex-wrap items-center gap-2 text-sm md:text-base">
              <Zap className="w-4 h-4 md:w-5 md:h-5 text-yellow-400" />
              Weekly Generator
              <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">
                2/Week
              </span>
            </h4>
            <p className="text-xs md:text-sm text-neutral-400 mt-1">
              AI generates video scripts for your review
            </p>
          </div>
          <Button
            onClick={generateWeeklyContent}
            disabled={generatingWeekly}
            size="sm"
            className="bg-gradient-to-r from-red-600 to-[#00F0FF] hover:opacity-90 whitespace-nowrap"
          >
            {generatingWeekly ? (
              <>
                <RefreshCw className="w-4 h-4 mr-1 md:mr-2 animate-spin" />
                <span className="text-sm">Generating...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-1 md:mr-2" />
                <span className="text-sm">Generate</span>
              </>
            )}
          </Button>
        </div>
        
        {/* Trending Topics Preview */}
        <div className="bg-neutral-900/50 rounded-lg p-3 md:p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs md:text-sm text-neutral-400 flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-green-400" />
              Trending AI Topics
            </span>
            <Button size="sm" variant="ghost" onClick={loadTrendingTopics} disabled={loadingTrending}>
              {loadingTrending ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Refresh'}
            </Button>
          </div>
          {trendingTopics.length > 0 ? (
            <div className="space-y-2">
              {trendingTopics.slice(0, 3).map((t, i) => (
                <div
                  key={t.id || t.url || `${t.source || 'src'}-${(t.title || '').slice(0, 20)}-${i}`}
                  className="flex items-start gap-2 text-sm"
                >
                  <span className="text-green-400 font-bold">{i + 1}.</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-neutral-300 line-clamp-1 text-xs md:text-sm">{t.title}</p>
                    <p className="text-xs text-neutral-500">{t.source}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs md:text-sm text-neutral-500">Click Refresh to see trending topics</p>
          )}
        </div>
        
        <div className="mt-3 md:mt-4 text-xs text-neutral-500">
          <strong>Schedule:</strong> Tue 6PM & Thu 3PM EST
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
        <div className="flex gap-2 border-b border-white/10 pb-2 min-w-max md:min-w-0">
          <button
            onClick={() => setActiveTab('content')}
            className={`px-3 md:px-4 py-2 rounded-t-lg font-medium text-sm whitespace-nowrap ${
              activeTab === 'content' ? 'bg-neutral-800 text-white' : 'text-neutral-400 hover:text-white'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-1 md:mr-2" />
            Content
          </button>
          <button
            onClick={() => setActiveTab('queue')}
            className={`px-3 md:px-4 py-2 rounded-t-lg font-medium text-sm whitespace-nowrap ${
              activeTab === 'queue' ? 'bg-neutral-800 text-white' : 'text-neutral-400 hover:text-white'
            }`}
          >
            <ListChecks className="w-4 h-4 inline mr-1 md:mr-2" />
            Queue
            {(contentQueue.pending_review?.length || 0) > 0 && (
              <span className="ml-1 md:ml-2 px-1.5 py-0.5 text-xs bg-orange-500 text-white rounded-full">
                {contentQueue.pending_review.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* AI Content Generator - Manual */}
      {activeTab === 'content' && (
        <div className="bg-neutral-800/50 rounded-xl p-6">
          <h4 className="font-bold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-white" />
            Generate Single Video
          </h4>
          <div className="flex gap-3">
            <Input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Enter topic (or leave blank for daily AI news)"
              className="bg-neutral-900 border-white/10 flex-1"
            />
            <Button
              onClick={generateContent}
              disabled={generating}
              className="bg-white text-black hover:bg-neutral-200"
            >
              {generating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Generate Script
            </Button>
          </div>
        </div>
      )}

      {/* Review Queue */}
      {activeTab === 'queue' && (
        <div className="bg-neutral-800/50 rounded-xl p-6">
          <h4 className="font-bold mb-4 flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-orange-500" />
            Content Review Queue
          </h4>
          
          {/* Pending Review */}
          {contentQueue.pending_review?.length > 0 && (
            <div className="mb-6">
              <h5 className="text-sm font-medium text-orange-400 mb-3">Pending Review ({contentQueue.pending_review.length})</h5>
              <div className="space-y-3">
                {contentQueue.pending_review.map((item) => (
                  <div key={item.content_id} className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h6 className="font-medium">{item.title}</h6>
                        <p className="text-xs text-neutral-400 mt-1">{formatDate(item.created_at)}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            const fullContent = content.find(c => c.content_id === item.content_id);
                            setSelectedContent(fullContent || item);
                            setActiveTab('content');
                          }}
                          variant="outline"
                          className="border-white/20"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => approveContent(item.content_id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckSquare className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Approved - Ready for Video */}
          {contentQueue.approved?.length > 0 && (
            <div className="mb-6">
              <h5 className="text-sm font-medium text-green-400 mb-3">Approved - Ready for Video ({contentQueue.approved.length})</h5>
              <div className="space-y-3">
                {contentQueue.approved.map((item) => (
                  <div key={item.content_id} className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h6 className="font-medium">{item.title}</h6>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-neutral-400">{formatDate(item.created_at)}</span>
                          {item.video_status === 'ready' && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-[#00F0FF]/20 text-[#00F0FF]">Video Ready</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {item.video_status !== 'ready' ? (
                          <Button
                            size="sm"
                            onClick={() => generateVideo(item.content_id)}
                            disabled={generatingVideo}
                            className="bg-white text-black hover:bg-neutral-200"
                          >
                            <Video className="w-4 h-4 mr-1" />
                            Generate Video
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => {
                              const fullContent = content.find(c => c.content_id === item.content_id);
                              setSelectedContent(fullContent || item);
                              setActiveTab('content');
                            }}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            Schedule/Upload
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Scheduled */}
          {contentQueue.scheduled?.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-yellow-400 mb-3">Scheduled ({contentQueue.scheduled.length})</h5>
              <div className="space-y-3">
                {contentQueue.scheduled.map((item) => (
                  <div key={item.content_id} className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h6 className="font-medium">{item.title}</h6>
                        <p className="text-xs text-yellow-400 mt-1">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {formatDate(item.scheduled_at)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => cancelSchedule(item.content_id)}
                        variant="outline"
                        className="border-yellow-500 text-yellow-400"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {contentQueue.pending_review?.length === 0 && contentQueue.approved?.length === 0 && contentQueue.scheduled?.length === 0 && (
            <div className="text-center py-8 text-neutral-400">
              <ListChecks className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No items in queue. Generate weekly content to get started!</p>
            </div>
          )}
        </div>
      )}

      {/* Content List & Editor */}
      {activeTab === 'content' && (
        <div className="grid lg:grid-cols-2 gap-6">
        {/* Content List */}
        <div className="bg-neutral-800/50 rounded-xl p-6">
          <h4 className="font-bold mb-4">Content Library</h4>
          {content.length === 0 ? (
            <p className="text-neutral-400 text-center py-8">No content yet. Generate some!</p>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {content.map((item) => (
                <div
                  key={item.content_id}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedContent?.content_id === item.content_id
                      ? 'border-[#00F0FF] bg-[#00F0FF]/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  onClick={() => { setSelectedContent(item); setEditMode(false); setShowScheduler(false); }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h5 className="font-medium truncate">{item.title}</h5>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-neutral-400">
                          {formatDate(item.created_at)}
                        </span>
                        {item.video_status === 'ready' && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-[#00F0FF]/20 text-[#00F0FF]">
                            Video Ready
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      item.status === 'published'
                        ? 'bg-green-500/20 text-green-400'
                        : item.status === 'scheduled'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-neutral-500/20 text-neutral-400'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Content Editor/Viewer */}
        <div className="bg-neutral-800/50 rounded-xl p-6">
          {selectedContent ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold">Content Details</h4>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditMode(!editMode)}
                    className="border-white/20"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteContent(selectedContent.content_id)}
                    className="border-red-500 text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {editMode ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-neutral-400">Title</label>
                    <Input
                      value={selectedContent.title}
                      onChange={(e) => setSelectedContent({...selectedContent, title: e.target.value})}
                      className="bg-neutral-900 border-white/10 mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-neutral-400">Description</label>
                    <Textarea
                      value={selectedContent.description}
                      onChange={(e) => setSelectedContent({...selectedContent, description: e.target.value})}
                      className="bg-neutral-900 border-white/10 mt-1"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-neutral-400">Script</label>
                    <Textarea
                      value={selectedContent.script}
                      onChange={(e) => setSelectedContent({...selectedContent, script: e.target.value})}
                      className="bg-neutral-900 border-white/10 mt-1"
                      rows={6}
                    />
                  </div>
                  <Button onClick={updateContent} className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black w-full">
                    Save Changes
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Video Section */}
                  <div className="bg-neutral-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium flex items-center gap-2">
                        <Video className="w-4 h-4 text-white" />
                        Video Status
                      </span>
                      {selectedContent.video_status === 'ready' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                          Ready
                        </span>
                      )}
                      {selectedContent.video_status === 'generating' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">
                          Generating...
                        </span>
                      )}
                      {selectedContent.video_status === 'failed' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
                          Failed
                        </span>
                      )}
                    </div>

                    {selectedContent.video_url && selectedContent.video_status === 'ready' ? (
                      <div className="space-y-2">
                        <video
                          controls
                          className="w-full rounded-lg max-h-[200px] bg-black"
                          src={`${API_URL}${selectedContent.video_url}`}
                        />
                        <div className="flex gap-2">
                          <a
                            href={`${API_URL}${selectedContent.video_url}`}
                            download
                            className="flex-1"
                          >
                            <Button size="sm" variant="outline" className="w-full border-white/20">
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </Button>
                          </a>
                          <Button
                            size="sm"
                            onClick={() => generateVideo(selectedContent.content_id)}
                            disabled={generatingVideo}
                            className="bg-white text-black hover:bg-neutral-200"
                          >
                            <RefreshCw className={`w-4 h-4 ${generatingVideo ? 'animate-spin' : ''}`} />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        onClick={() => generateVideo(selectedContent.content_id)}
                        disabled={generatingVideo}
                        className="w-full bg-white text-black hover:bg-neutral-200"
                      >
                        {generatingVideo ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Generating with Sora 2...
                          </>
                        ) : (
                          <>
                            <Video className="w-4 h-4 mr-2" />
                            Generate Video (Sora 2)
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  {/* Upload Section */}
                  {selectedContent.video_status === 'ready' && selectedContent.status !== 'published' && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <h5 className="font-medium text-red-400 mb-3 flex items-center gap-2">
                        <Youtube className="w-4 h-4" />
                        Upload to YouTube
                      </h5>
                      
                      {showScheduler ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm">Schedule for:</label>
                            <button onClick={() => setShowScheduler(false)} className="text-neutral-400 hover:text-white">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <Input
                            type="datetime-local"
                            value={scheduleDate}
                            onChange={(e) => setScheduleDate(e.target.value)}
                            className="bg-neutral-900 border-white/10"
                          />
                          <Button
                            onClick={() => scheduleUpload(selectedContent.content_id)}
                            className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
                          >
                            <Calendar className="w-4 h-4 mr-2" />
                            Schedule Upload
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => uploadToYouTube(selectedContent.content_id)}
                            disabled={uploading || !youtubeConnected}
                            className="flex-1 bg-red-600 hover:bg-red-700"
                          >
                            {uploading ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Now
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => setShowScheduler(true)}
                            variant="outline"
                            className="border-yellow-500 text-yellow-400"
                          >
                            <Calendar className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {!youtubeConnected && (
                        <p className="text-xs text-red-400 mt-2">
                          Connect YouTube first to upload videos
                        </p>
                      )}
                    </div>
                  )}

                  {/* Scheduled Info */}
                  {selectedContent.status === 'scheduled' && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-yellow-400 font-medium">Scheduled Upload</p>
                          <p className="text-xs text-neutral-400">{formatDate(selectedContent.scheduled_at)}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => cancelSchedule(selectedContent.content_id)}
                          className="border-yellow-500 text-yellow-400"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Published Info */}
                  {selectedContent.status === 'published' && selectedContent.youtube_url && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-green-400 font-medium">Published on YouTube</p>
                          <p className="text-xs text-neutral-400">{formatDate(selectedContent.published_at)}</p>
                        </div>
                        <a
                          href={selectedContent.youtube_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button size="sm" className="bg-red-600 hover:bg-red-700">
                            <ExternalLink className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Content Info */}
                  <div>
                    <label className="text-sm text-neutral-400">Title</label>
                    <p className="font-medium">{selectedContent.title}</p>
                  </div>
                  <div>
                    <label className="text-sm text-neutral-400">Description</label>
                    <p className="text-sm text-neutral-300 line-clamp-3">{selectedContent.description}</p>
                  </div>
                  <div>
                    <label className="text-sm text-neutral-400">Script Preview</label>
                    <div className="bg-neutral-900 p-3 rounded-lg max-h-32 overflow-y-auto text-sm">
                      {selectedContent.script?.substring(0, 500)}
                    </div>
                  </div>
                  {selectedContent.tags?.length > 0 && (
                    <div>
                      <label className="text-sm text-neutral-400">Tags</label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedContent.tags.slice(0, 10).map((tag, i) => (
                          <span
                            key={`${tag}-${i}`}
                            className="px-2 py-0.5 bg-[#00F0FF]/10 text-[#00F0FF] text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notify Button */}
                  {selectedContent.status === 'published' && (
                    <Button
                      onClick={() => notifySubscribers(selectedContent.content_id)}
                      variant="outline"
                      className="w-full border-[#00F0FF] text-[#00F0FF]"
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Notify Subscribers
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-neutral-400 py-12">
              <Youtube className="w-12 h-12 mx-auto mb-4 opacity-50 text-red-500" />
              <p>Select content to view details</p>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

export default YouTubeAdmin;
